import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import { useSEO } from '@/hooks/useSEO';

const brands = [
  {
    name: 'Pandim',
    category: 'Mesas · Estações de Trabalho · Móveis de Aço',
    description: 'A Pandim é referência em mobiliário corporativo, oferecendo soluções completas em mesas, estações de trabalho e móveis de aço para escritórios modernos. Qualidade, funcionalidade e design em cada produto.',
    highlights: ['Mesas corporativas', 'Estações de trabalho', 'Móveis de aço', 'Garantia de qualidade'],
    image: 'https://www.pandin.com.br/wp-content/uploads/2025/05/03-Escritorio-Alto-Padrao-PE40-TUB-Preto-Salvia-1024x768.jpg',
    path: '/pandim',
  },
  {
    name: 'Frisokar',
    category: 'Cadeiras de Escritório',
    description: 'Referência nacional em cadeiras ergonômicas, a Frisokar combina tecnologia avançada com design sofisticado. Com mais de 30 anos de mercado, seus produtos são desenvolvidos com foco total no bem-estar do usuário.',
    highlights: ['Certificação NR17', 'Garantia de 5 anos', '10 modelos disponíveis', 'Entrega em todo Brasil'],
    image: 'https://www.frisokar.com.br/wp-content/uploads/2024/06/5175.jpg',
    path: '/frisokar',
  },
  {
    name: 'Plaxmetal',
    category: 'Cadeiras Corporativas',
    description: 'Com décadas de experiência, a Plaxmetal é sinônimo de qualidade e durabilidade no segmento de cadeiras corporativas. Seus produtos atendem desde pequenos escritórios até grandes corporações.',
    highlights: ['Garantia de 5 anos', 'Mecanismo Back System', '6 modelos disponíveis', 'Suporte técnico'],
    image: 'https://dcdn-us.mitiendanube.com/stores/004/083/073/products/ab532543-c89b-4ae8-b0c2-e02a6f122f90-3808c7b1d21b7f528e17571994253961-1024-1024.webp',
    path: '/plaxmetal',
  },
  {
    name: 'Marzo Vitorino',
    category: 'Mesas e Estações de Trabalho',
    description: 'Especialista em mesas corporativas, a Marzo Vitorino oferece soluções completas para escritórios modernos. Do design à entrega, cada produto é pensado para unir funcionalidade, estética e durabilidade.',
    highlights: ['Design exclusivo', 'Garantia de 5 anos', '16 linhas disponíveis', 'Montagem inclusa'],
    image: 'https://www.marzovitorino.com.br/imgs/attualehomedesktop.jpg',
    path: '/marzo-vitorino',
  },
  {
    name: 'W3 Indústria',
    category: 'Mobiliários de Aço',
    description: 'Líder em mobiliário corporativo de aço, a W3 Indústria oferece armários, estantes, arquivos e lockers com pintura eletrostática a pó e tratamento antiferrugem. Produtos que atendem às normas NR18 e NR24.',
    highlights: ['Normas NR18 e NR24', 'Pintura eletrostática', '6 modelos disponíveis', 'Tratamento antiferrugem'],
    image: 'https://w3.ind.br/storage/products/armarios/soldados/ws20307/ws20307-1.jpg',
    path: '/w3-industria',
  },
  {
    name: 'Rivatti',
    category: 'Restaurante · Mesas · Decoração · Infantil · Outdoor',
    description: 'Uma das maiores fabricantes de móveis para food service do Brasil. A Rivatti oferece cadeiras para restaurantes, mesas, linha de decoração, mobiliário infantil e a linha outdoor para áreas externas — tudo com design, resistência e custo-benefício.',
    highlights: ['5 linhas de produtos', 'Linha Outdoor UV resistente', 'Linha Infantil atóxica', 'Fabricação nacional'],
    image: 'https://www.rivatti.com.br/_files/subcategoria/cadeira_norma_verde_floresta_07.jpg',
    path: '/rivatti',
  },
];

export default function MarcasPage() {
  useSEO({
    title: 'Marcas Parceiras | Pandim, Frisokar, Plaxmetal, Marzo Vitorino, W3, Rivatti – Home Office Premium Caruaru',
    description: 'Conheça as marcas parceiras da Home Office Premium em Caruaru PE: Pandim, Frisokar, Plaxmetal, Marzo Vitorino, W3 e Rivatti. Líderes em móveis corporativos e food service.',
    keywords: 'Pandim Caruaru, Frisokar Caruaru, Plaxmetal Pernambuco, Rivatti revendedor, Marzo Vitorino, W3 Indústria, marcas móveis corporativos',
    path: '/marcas',
    ogTitle: 'Marcas Parceiras | Home Office Premium Caruaru',
    ogDescription: 'Frisokar, Plaxmetal, Marzo Vitorino, W3 e Rivatti em Caruaru PE.',
  });

  useEffect(() => {
    const schemaId = 'schema-marcas';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Marcas Parceiras – Home Office Premium',
      description: 'Marcas parceiras da Home Office Premium em Caruaru PE: Pandim, Frisokar, Plaxmetal, Marzo Vitorino, W3 e Rivatti.',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/marcas`,
    });

    const breadcrumbId = 'schema-marcas-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? '' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? ''}/marcas` },
      ],
    });

    const itemListId = 'schema-marcas-itemlist';
    let itemListScript = document.getElementById(itemListId) as HTMLScriptElement | null;
    if (!itemListScript) { itemListScript = document.createElement('script'); itemListScript.id = itemListId; itemListScript.type = 'application/ld+json'; document.head.appendChild(itemListScript); }
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Marcas Parceiras – Home Office Premium',
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/marcas`,
      numberOfItems: brands.length,
      itemListElement: brands.map((brand, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Brand',
          name: brand.name,
          description: brand.description,
          url: `${import.meta.env.VITE_SITE_URL ?? ''}${brand.path}`,
        },
      })),
    });

    const faqId = 'schema-marcas-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Quais marcas de móveis corporativos a Home Office Premium representa em Caruaru?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium é revendedora autorizada das marcas Pandim, Frisokar, Plaxmetal, Marzo Vitorino, W3 Indústria e Rivatti em Caruaru e todo Pernambuco. São as principais marcas do mercado brasileiro de móveis corporativos.' },
        },
        {
          '@type': 'Question',
          name: 'A Frisokar é uma boa marca de cadeiras ergonômicas?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. A Frisokar é referência nacional em cadeiras ergonômicas com mais de 30 anos de mercado. Seus produtos possuem certificação NR-17, garantia de 5 anos e são desenvolvidos com foco total no bem-estar do usuário.' },
        },
        {
          '@type': 'Question',
          name: 'Onde comprar móveis Marzo Vitorino em Pernambuco?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium é revendedora autorizada Marzo Vitorino em Caruaru PE, atendendo todo o estado de Pernambuco. Temos toda a linha disponível com entrega, montagem e suporte técnico especializado.' },
        },
        {
          '@type': 'Question',
          name: 'A W3 Indústria atende normas de segurança do trabalho?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Os móveis de aço W3 Indústria atendem às normas NR18 e NR24, com pintura eletrostática a pó e tratamento antiferrugem. São ideais para escritórios, indústrias e ambientes que exigem durabilidade e segurança.' },
        },
      ],
    });

    return () => { script?.remove(); bcScript?.remove(); itemListScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 pt-32 md:pt-40 pb-12">
          <SectionHeading
            eyebrow="Parceiros"
            title={
              <>
                Marcas que <span className="font-light text-foreground-400">confiamos.</span>
              </>
            }
            subtitle="Trabalhamos exclusivamente com marcas líderes de mercado, garantindo qualidade, durabilidade e suporte para nossos clientes."
          />
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-16 md:py-20 space-y-20 md:space-y-24">
          {brands.map((brand, idx) => (
            <div key={brand.name} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
              <div className={`img-zoom rounded-2xl overflow-hidden bg-background-100 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <img
                  src={brand.image}
                  alt={brand.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top aspect-[4/3]"
                />
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <p className="text-primary-600 text-[11px] font-semibold uppercase tracking-[0.15em] mb-3">{brand.category}</p>
                <h2 className="text-foreground-900 font-semibold text-[26px] md:text-[32px] tracking-tight">{brand.name}</h2>
                <p className="mt-4 text-foreground-500 text-[15px] leading-relaxed font-light">{brand.description}</p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {brand.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2">
                      <i className="ri-check-line text-primary-500 text-sm"></i>
                      <span className="text-foreground-700 text-[14px]">{h}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={brand.path}
                  className="mt-8 inline-flex items-center gap-2 text-primary-600 font-medium text-[15px] hover:gap-3 transition-all duration-300 cursor-pointer"
                >
                  Ver produtos desta marca <i className="ri-arrow-right-line text-sm"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        title="Não sabe qual marca escolher?"
        subtitle="A gente te orienta. Fale com um especialista e descubra a melhor solução para o seu projeto."
        primaryLabel="Falar com um especialista"
        primaryHref="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}